/**
 * Squadre-stagione (V48): la matrice squadre per stagioni, dove ogni cella
 * esistente e l'entita a cui si agganciano rosa, staff e partite.
 *
 * Una cella vuota si riempie creando l'incrocio. Il limite di squadre per
 * stagione lo applica il server; qui lo si mostra in testa alla colonna e si
 * toglie il pulsante quando e raggiunto, cosi il rifiuto non arriva a
 * sorpresa. Dalla cella si aprono la squadra, le sue impostazioni (V43) e,
 * finche la rosa e vuota, l'import (V09).
 */

import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { OctagonXIcon, PlusIcon } from 'lucide-react';
import { creaSquadraStagioneSchema } from '@calcio/shared';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PulsanteInvio } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { useContesto } from '../navigazione/contesto.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { erroriPerCampo, messaggio, useDati } from '../comuni/dati.js';
import {
  type Matrice,
  type Squadra,
  type Stagione,
  righeMatrice,
  stagionePiena,
} from './matrice.js';

interface Incrocio {
  squadra: Squadra;
  stagione: Stagione;
}

export function SquadreStagione() {
  const { dati: matrice, errore, ricarica } = useDati<Matrice>('/amministrazione/squadre-stagione');
  const { ricaricaSquadre } = useContesto();
  const [daCreare, setDaCreare] = useState<Incrocio | null>(null);

  if (!matrice)
    return (
      <Corpo>
        <StatoCaricamento errore={errore} />
      </Corpo>
    );

  if (matrice.stagioni.length === 0 || !matrice.squadre.some((s) => s.attiva)) {
    const manca = matrice.stagioni.length === 0 ? 'stagioni' : 'squadre';
    return (
      <>
        <Testata titolo="Squadre-stagione" voce="squadre-stagione" />
        <Corpo>
          <Empty>
            <EmptyHeader>
              <EmptyTitle>Servono stagioni e squadre</EmptyTitle>
              <EmptyDescription>
                Ogni cella incrocia una squadra con una stagione: prima vanno create le {manca}.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button asChild>
                <Link to={`/amministrazione/${manca}`}>
                  Vai a {manca === 'stagioni' ? 'Stagioni' : 'Squadre'}
                </Link>
              </Button>
            </EmptyContent>
          </Empty>
        </Corpo>
      </>
    );
  }

  const righe = righeMatrice(matrice);

  return (
    <>
      <Testata titolo="Squadre-stagione" voce="squadre-stagione" />
      <Corpo>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Squadra</TableHead>
              {matrice.stagioni.map((s) => (
                <TableHead key={s.id} className="align-bottom">
                  <span className="flex flex-col gap-0.5 py-1">
                    <span className="flex items-center gap-2">
                      {s.nome}
                      {s.corrente && <Badge>Corrente</Badge>}
                    </span>
                    <span className="text-xs font-normal text-muted-foreground tabular-nums">
                      {stagionePiena(s, matrice.limite)
                        ? `Limite raggiunto · ${s.squadre}/${matrice.limite}`
                        : `${s.squadre}/${matrice.limite} squadre`}
                    </span>
                  </span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {righe.map(({ squadra, celle }) => (
              <TableRow key={squadra.id}>
                <TableCell className="font-medium">
                  <span className="flex items-center gap-2">
                    {squadra.nome}
                    {!squadra.attiva && <Badge variant="secondary">Disattivata</Badge>}
                  </span>
                </TableCell>
                {celle.map((cella, i) => {
                  const stagione = matrice.stagioni[i]!;
                  if (cella) {
                    return (
                      <TableCell key={stagione.id}>
                        <span className="flex flex-col items-start gap-1">
                          <Link
                            to={`/sq/${cella.id}`}
                            className="flex flex-col underline-offset-4 hover:underline"
                          >
                            <span>{cella.categoria ?? 'Senza categoria'}</span>
                            <span className="text-xs text-muted-foreground tabular-nums">
                              {cella.tesserati} tesserati
                            </span>
                          </Link>
                          <span className="flex flex-wrap gap-x-3">
                            <Button asChild variant="link" size="sm" className="h-auto px-0">
                              <Link to={`/sq/${cella.id}/impostazioni`}>Impostazioni</Link>
                            </Button>
                            {cella.tesserati === 0 && (
                              <Button asChild variant="link" size="sm" className="h-auto px-0">
                                <Link to={`/sq/${cella.id}/rosa/import`}>Importa rosa</Link>
                              </Button>
                            )}
                          </span>
                        </span>
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell key={stagione.id}>
                      {squadra.attiva && !stagionePiena(stagione, matrice.limite) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDaCreare({ squadra, stagione })}
                        >
                          <PlusIcon data-icon="inline-start" />
                          Aggiungi
                        </Button>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Dialog open={daCreare !== null} onOpenChange={(aperto) => !aperto && setDaCreare(null)}>
          <DialogContent>
            {daCreare && (
              <FormIncrocio
                key={`${daCreare.squadra.id}|${daCreare.stagione.id}`}
                incrocio={daCreare}
                onChiudi={() => setDaCreare(null)}
                onCreato={async () => {
                  setDaCreare(null);
                  await Promise.all([ricarica(), ricaricaSquadre()]);
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </Corpo>
    </>
  );
}

function FormIncrocio({
  incrocio: { squadra, stagione },
  onChiudi,
  onCreato,
}: {
  incrocio: Incrocio;
  onChiudi: () => void;
  onCreato: () => Promise<void>;
}) {
  const [categoria, setCategoria] = useState('');
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = creaSquadraStagioneSchema.safeParse({
      squadraId: squadra.id,
      stagioneId: stagione.id,
      categoria,
    });
    if (!esito.success) return setErrori(erroriPerCampo(esito.error));
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      await chiama('/amministrazione/squadre-stagione', { metodo: 'POST', corpo: esito.data });
      await onCreato();
      toast.success(`${squadra.nome} aggiunta a ${stagione.nome}.`);
    } catch (errore) {
      setErroreServer(messaggio(errore));
      setInCorso(false);
    }
  };

  return (
    <form onSubmit={(e) => void invia(e)} className="flex flex-col gap-6">
      <DialogHeader>
        <DialogTitle>
          {squadra.nome} · {stagione.nome}
        </DialogTitle>
        <DialogDescription>
          Formato di gioco ed eventi del Live Timer partono dai valori predefiniti.
        </DialogDescription>
      </DialogHeader>

      <FieldGroup>
        <Field data-invalid={Boolean(errori.categoria)}>
          <FieldLabel htmlFor="incrocio-categoria">Categoria</FieldLabel>
          <Input
            id="incrocio-categoria"
            value={categoria}
            placeholder="Under 15"
            aria-invalid={Boolean(errori.categoria)}
            onChange={(e) => setCategoria(e.target.value)}
          />
          <FieldDescription>Facoltativa.</FieldDescription>
          <FieldError>{errori.categoria}</FieldError>
        </Field>
      </FieldGroup>

      {erroreServer && (
        <Alert variant="destructive">
          <OctagonXIcon />
          <AlertTitle>Salvataggio non riuscito</AlertTitle>
          <AlertDescription>{erroreServer}</AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onChiudi}>
          Annulla
        </Button>
        <PulsanteInvio type="submit" inCorso={inCorso}>
          Crea squadra-stagione
        </PulsanteInvio>
      </DialogFooter>
    </form>
  );
}
